/**
 * /mp3-to-video's picture (SEO_BRIEF_12 section 4), drawn once on the page's own canvas at the
 * video's size, then handed to the worker as the frame it repeats.
 *
 *   With a picture   the whole picture, as large as fits, centred on Obsidian (#070709). No title.
 *   Without          Obsidian, with the title (the file's name until the visitor edits it) in
 *                    Paper White, Open Runde 600, centred: at most three lines, at 9% of the
 *                    frame's height for a short title down to 4.5% for a long one, and cut with
 *                    an ellipsis past that.
 * web/DESIGN_SYSTEM.md's colours and type, and none of its off-limits ones: the sky gradient is
 * the hero's only, Caption Yellow the logo's.
 *
 * The layout is pure arithmetic over a text measurer, as lib/layout.ts places subtitles, and
 * wraps with its `tokenize` and `wrapTokens`: words at spaces, CJK text between characters.
 */

import { tokenize, wrapTokens } from './layout.ts';

export const OBSIDIAN = '#070709';
export const PAPER_WHITE = '#ffffff';
/** The site's --font-sans: Open Runde, then the system stack it falls back to. */
export const TITLE_FAMILY =
  '"Open Runde", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const TITLE_WEIGHT = 600;
export const TITLE_MAX_LINES = 3;
/** The title's size as a share of the frame's height, largest first. */
export const TITLE_SIZE_MAX = 0.09;
export const TITLE_SIZE_MIN = 0.045;
/** Steps between the two: 9%, 8.5% … 4.5%. */
export const TITLE_SIZE_STEP = 0.005;
/** The Title role's line height (DESIGN_SYSTEM.md). */
export const TITLE_LINE_HEIGHT = 1.1;
/** The width a line may take, as a share of the frame's. */
export const TITLE_WIDTH = 0.84;
/** DESIGN_SYSTEM.md's tracking at 28px and above, as a share of the size. */
export const TITLE_TRACKING = -0.04;
export const ELLIPSIS = '…';

export interface FrameSize {
  readonly width: number;
  readonly height: number;
}

/** Width in pixels of `text` at `px` pixels in the title's font. */
export type MeasureTitle = (text: string, px: number) => number;

export interface TitleLine {
  readonly text: string;
  /** The line's centre. */
  readonly x: number;
  readonly y: number;
}

export interface TitleLayout {
  readonly px: number;
  readonly font: string;
  readonly lines: readonly TitleLine[];
}

export function titleFont(px: number): string {
  return `${TITLE_WEIGHT} ${Math.round(px * 100) / 100}px ${TITLE_FAMILY}`;
}

/** The sizes tried, largest first, in pixels for a frame `height` tall. */
export function titleSizes(height: number): number[] {
  const out: number[] = [];
  const steps = Math.round((TITLE_SIZE_MAX - TITLE_SIZE_MIN) / TITLE_SIZE_STEP);
  for (let i = 0; i <= steps; i++) out.push(height * (TITLE_SIZE_MAX - i * TITLE_SIZE_STEP));
  return out;
}

/** `text` cut from the end, with an ellipsis, until it fits `maxWidth`. */
function cut(text: string, px: number, maxWidth: number, measure: MeasureTitle): string {
  if (measure(text, px) <= maxWidth) return text;
  const chars = Array.from(text.trimEnd());
  while (chars.length > 0) {
    chars.pop();
    const tried = `${chars.join('').trimEnd()}${ELLIPSIS}`;
    if (measure(tried, px) <= maxWidth) return tried;
  }
  return ELLIPSIS;
}

/** The lines of `text` at `px`, greedy at the width allowed. */
function wrapAt(text: string, px: number, maxWidth: number, measure: MeasureTitle): string[] {
  const tokens = tokenize(text).map((t) => ({ ...t, width: measure(t.text, px) }));
  return wrapTokens(tokens, maxWidth).map((line) => line.map((t) => t.text).join(''));
}

/**
 * Where the title goes on a frame: the largest size at which it fits three lines, or three lines
 * at the smallest size with the third cut short. Null for an empty title, which draws nothing.
 */
export function layoutTitle(
  text: string,
  frame: FrameSize,
  measure: MeasureTitle,
): TitleLayout | null {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) return null;
  const maxWidth = frame.width * TITLE_WIDTH;
  const sizes = titleSizes(frame.height);
  let px = sizes[sizes.length - 1] as number;
  let lines: string[] | null = null;
  for (const size of sizes) {
    const wrapped = wrapAt(clean, size, maxWidth, measure);
    if (
      wrapped.length <= TITLE_MAX_LINES &&
      wrapped.every((line) => measure(line, size) <= maxWidth)
    ) {
      px = size;
      lines = wrapped;
      break;
    }
  }
  if (lines === null) {
    // Too long at the smallest size: three lines, the last one ending in an ellipsis. A word
    // wider than the frame on its own (a name with no spaces) is cut the same way.
    const wrapped = wrapAt(clean, px, maxWidth, measure);
    const kept = wrapped.slice(0, TITLE_MAX_LINES);
    if (wrapped.length > TITLE_MAX_LINES) {
      const last = kept.length - 1;
      kept[last] = cut(`${kept[last]}${ELLIPSIS}`, px, maxWidth, measure);
    }
    lines = kept.map((line) => cut(line, px, maxWidth, measure));
  }
  const lineHeight = px * TITLE_LINE_HEIGHT;
  const top = (frame.height - lineHeight * lines.length) / 2;
  return {
    px,
    font: titleFont(px),
    lines: lines.map((line, i) => ({
      text: line,
      x: frame.width / 2,
      y: top + lineHeight * (i + 0.5),
    })),
  };
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** A picture `width` × `height` at the largest size that fits the frame whole, centred. */
export function containRect(width: number, height: number, frame: FrameSize): Rect {
  if (!(width > 0) || !(height > 0))
    return { x: 0, y: 0, width: frame.width, height: frame.height };
  const scale = Math.min(frame.width / width, frame.height / height);
  const w = width * scale;
  const h = height * scale;
  return { x: (frame.width - w) / 2, y: (frame.height - h) / 2, width: w, height: h };
}

/** What a frame shows: a picture, or the title. */
export interface StillContent {
  readonly picture: {
    readonly source: CanvasImageSource;
    readonly width: number;
    readonly height: number;
  } | null;
  readonly title: string;
}

/** The parts of a 2D context the drawing uses, so a test can stand in for a canvas. */
export interface StillContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  font: string;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  imageSmoothingEnabled: boolean;
  imageSmoothingQuality: ImageSmoothingQuality;
  letterSpacing?: string;
  fillRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number): void;
  measureText(text: string): { width: number };
  drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
}

function setTitleType(ctx: StillContext, px: number): void {
  ctx.font = titleFont(px);
  // Canvas tracking where the browser has it (Chrome, Safari 18); where it doesn't, the
  // property is ignored for drawing and measuring alike, so the layout still holds.
  if ('letterSpacing' in ctx)
    ctx.letterSpacing = `${Math.round(TITLE_TRACKING * px * 100) / 100}px`;
}

/** Draw `content` on a frame-sized context. */
export function drawStill(ctx: StillContext, frame: FrameSize, content: StillContent): void {
  ctx.fillStyle = OBSIDIAN;
  ctx.fillRect(0, 0, frame.width, frame.height);
  const { picture } = content;
  if (picture) {
    const r = containRect(picture.width, picture.height, frame);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(picture.source, r.x, r.y, r.width, r.height);
    return;
  }
  const layout = layoutTitle(content.title, frame, (text, px) => {
    setTitleType(ctx, px);
    return ctx.measureText(text).width;
  });
  if (!layout) return;
  setTitleType(ctx, layout.px);
  ctx.fillStyle = PAPER_WHITE;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const line of layout.lines) ctx.fillText(line.text, line.x, line.y);
}
